import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketStore } from '../stores/socketStore';
import type { HomeLiveState, HomeLiveAnswerResult } from '@shared/homeLive';

type Feedback = HomeLiveAnswerResult['status'] | 'pending' | 'unconfirmed' | null;
let sequence = 0;
export function useHomeLive() {
  const { socket, isConnected, currentRoom, connect } = useSocketStore();
  const [state, setState] = useState<HomeLiveState | null>(null);
  const [offset, setOffset] = useState(0);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const current = useRef<HomeLiveState | null>(null);
  const pending = useRef<{roundId:string; requestId:string} | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    current.current = null; setState(null); setFeedback(null); pending.current = null; clearTimeout(timer.current);
    if (!socket || !isConnected || currentRoom) return;
    const receive = (next: HomeLiveState) => {
      if (current.current && next.revision < current.current.revision) return;
      if (next.roundId !== current.current?.roundId || next.phase !== 'listening') { pending.current = null; clearTimeout(timer.current); setFeedback(null); }
      current.current = next; setOffset(next.serverNow - Date.now()); setState(next);
    };
    const result = (reply: HomeLiveAnswerResult) => {
      if (!pending.current || reply.roundId !== current.current?.roundId || reply.roundId !== pending.current.roundId || reply.requestId !== pending.current.requestId) return;
      pending.current = null; clearTimeout(timer.current); setFeedback(reply.status);
    };
    socket.on('homeLive:state', receive); socket.on('homeLive:answerResult', result); socket.emit('homeLive:subscribe');
    return () => { socket.emit('homeLive:unsubscribe'); socket.off('homeLive:state', receive); socket.off('homeLive:answerResult', result); clearTimeout(timer.current); pending.current = null; };
  }, [socket, isConnected, currentRoom]);
  const refresh = useCallback(() => { if (socket && isConnected && !currentRoom) socket.emit('homeLive:sync'); }, [socket, isConnected, currentRoom]);
  const retry = useCallback(() => { if (currentRoom) return; if (socket && isConnected) socket.emit('homeLive:sync'); else if (socket) socket.connect(); else connect(); }, [socket, isConnected, currentRoom, connect]);
  const submitAnswer = useCallback((answer: string) => {
    const round = current.current;
    if (!socket || !isConnected || currentRoom || !round?.roundId || round.phase !== 'listening' || !answer.trim() || pending.current) return;
    const requestId = `${Date.now().toString(36)}-${++sequence}`;
    pending.current = {roundId:round.roundId, requestId}; setFeedback('pending');
    socket.emit('homeLive:answer', {roundId:round.roundId, requestId, answer:answer.trim()});
    timer.current = setTimeout(() => { if (pending.current?.requestId === requestId) { pending.current = null; setFeedback('unconfirmed'); } }, 10000);
  }, [socket, isConnected, currentRoom]);
  return { state, offset, feedback, submitAnswer, refresh, retry, connected: isConnected && !currentRoom };
}
